/**
 * Photo slots use object-fit: cover + object-position from Brand Library focal_point
 * so images fill the frame without changing aspect ratio.
 */
export function focalPointToObjectPosition(focal) {
  if (!focal || typeof focal !== 'object') return '50% 50%';
  const x = Number(focal.x);
  const y = Number(focal.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return '50% 50%';
  const cx = Math.max(0, Math.min(100, Math.round(x * 100)));
  const cy = Math.max(0, Math.min(100, Math.round(y * 100)));
  return `${cx}% ${cy}%`;
}

function assetEntry(assetUrls, assetId) {
  if (assetId == null || assetId === '') return null;
  const n = Number(assetId);
  if (Number.isFinite(n)) {
    return assetUrls?.[n] || assetUrls?.[String(n)] || null;
  }
  return assetUrls?.[assetId] || null;
}

export function objectPositionForPhoto(assetUrls, assetId, fallbacks = []) {
  for (const fb of fallbacks) {
    if (fb) return fb;
  }
  const fp = assetId != null ? assetEntry(assetUrls, assetId)?.focal_point : null;
  if (fp) return focalPointToObjectPosition(fp);
  return '50% 50%';
}

export function parseObjectPosition(value) {
  const raw = String(value || '50% 50%').trim();
  const parts = raw.split(/\s+/);
  const parsePart = (p, fallback) => {
    if (!p) return fallback;
    if (p.endsWith('%')) {
      const n = Number(p.slice(0, -1));
      return Number.isFinite(n) ? n : fallback;
    }
    return fallback;
  };
  return {
    x: parsePart(parts[0], 50),
    y: parsePart(parts[1] || parts[0], 50),
  };
}

export function formatObjectPosition(x, y) {
  const cx = Math.max(0, Math.min(100, Math.round(x)));
  const cy = Math.max(0, Math.min(100, Math.round(y)));
  return `${cx}% ${cy}%`;
}

export const PHOTO_OBJECT_FIT = 'cover';

/** Transparent SVG/PNG cutouts are shown whole on whatever sits behind the slot. */
export const CUTOUT_OBJECT_FIT = 'contain';

export const ZOOM_ATTR = 'data-mc-zoom';
export const MIN_PHOTO_ZOOM = 1;
export const MIN_CUTOUT_ZOOM = 0.25;
export const MAX_PHOTO_ZOOM = 5;

const LAYOUT_ATTR = 'data-mc-layout';
const PCT = '-?\\d*\\.?\\d+%';
const ZOOM_SUFFIX_RE = new RegExp(
  `\\s*translate\\(\\s*${PCT}\\s*,\\s*${PCT}\\s*\\)\\s*scale\\(\\s*\\d*\\.?\\d+\\s*\\)`
  + `\\s*translate\\(\\s*${PCT}\\s*,\\s*${PCT}\\s*\\)\\s*$`,
);

/** Cover photos cannot zoom out (the frame would show empty space); cutouts can. */
export function clampPhotoZoom(value, { cutout = false } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.max(cutout ? MIN_CUTOUT_ZOOM : MIN_PHOTO_ZOOM, Math.min(MAX_PHOTO_ZOOM, n));
}

/** photo_zoom map with one slot updated; zoom 1 removes the entry. */
export function withSlotZoom(zoomMap, slotId, zoom) {
  const next = { ...(zoomMap || {}) };
  const z = Number(zoom);
  if (!Number.isFinite(z) || Math.abs(z - 1) < 1e-3) delete next[slotId];
  else next[slotId] = Math.round(z * 10000) / 10000;
  return next;
}

/** Remove the photo-zoom tail so only the element_layout part of a slot transform remains. */
export function stripZoomTransform(transform) {
  return String(transform || '').replace(ZOOM_SUFFIX_RE, '').trim();
}

function round4(n) {
  return Math.round(n * 10000) / 10000;
}

/**
 * Zoom slot content in place: scale about the object-position point and clip back to the frame,
 * so the img's layout box (and any element_layout move/scale) is unchanged.
 */
export function applyPhotoZoom(img, zoom, objectPosition) {
  if (!img) return;
  const base = stripZoomTransform(img.style.transform);
  const z = Number(zoom);
  if (!Number.isFinite(z) || Math.abs(z - 1) < 1e-3) {
    if (base) {
      img.style.transform = base;
    } else {
      img.style.removeProperty('transform');
      if (!img.hasAttribute(LAYOUT_ATTR)) img.style.removeProperty('transform-origin');
    }
    img.style.removeProperty('clip-path');
    img.removeAttribute(ZOOM_ATTR);
    return;
  }
  const { x, y } = parseObjectPosition(objectPosition || img.style.objectPosition || '50% 50%');
  const px = round4(x);
  const py = round4(y);
  const zoomPart = `translate(${px}%, ${py}%) scale(${round4(z)}) translate(${-px}%, ${-py}%)`;
  img.style.transformOrigin = 'top left';
  img.style.transform = base ? `${base} ${zoomPart}` : zoomPart;
  if (z > 1) {
    const k = 1 - 1 / z;
    const inset = [py * k, (100 - px) * k, (100 - py) * k, px * k].map((v) => `${round4(v)}%`);
    img.style.clipPath = `inset(${inset.join(' ')})`;
  } else {
    img.style.removeProperty('clip-path');
  }
  img.setAttribute(ZOOM_ATTR, String(round4(z)));
}

/** Read the zoom currently applied to a slot img (1 when none). */
export function appliedPhotoZoom(img) {
  const n = Number(img?.getAttribute?.(ZOOM_ATTR));
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Viewport rect of the slot frame for a zoomed img: undo the zoom about its centre point.
 * Exact for translate/scale transforms (no rotation).
 */
export function unzoomRect(rect, img) {
  const z = appliedPhotoZoom(img);
  if (!rect || z === 1) return rect;
  const { x, y } = parseObjectPosition(img.style.objectPosition || '50% 50%');
  const width = rect.width / z;
  const height = rect.height / z;
  const cx = rect.left + (rect.width * x) / 100;
  const cy = rect.top + (rect.height * y) / 100;
  const left = cx - (width * x) / 100;
  const top = cy - (height * y) / 100;
  return { left, top, width, height, right: left + width, bottom: top + height };
}

/** Decode inline SVG (Brand Library logos are often served as image/svg+xml data URLs). */
export function decodeSvgMarkupFromImageSrc(src) {
  const raw = String(src || '');
  if (!raw.includes('svg')) return null;
  if (raw.startsWith('data:image/svg+xml;base64,')) {
    try {
      return atob(raw.slice('data:image/svg+xml;base64,'.length));
    } catch {
      return null;
    }
  }
  if (raw.startsWith('data:image/svg+xml,')) {
    try {
      return decodeURIComponent(raw.slice('data:image/svg+xml,'.length));
    } catch {
      return null;
    }
  }
  return null;
}

/** viewBox / width+height from SVG markup (sync ingest stores dimensions in DB when available). */
export function parseSvgIntrinsicPixels(markup) {
  if (!markup) return null;
  const tag = markup.match(/<svg[\s\S]*?>/i)?.[0];
  if (!tag) return null;

  const viewBox = tag.match(/\bviewBox\s*=\s*["']([^"']+)["']/i);
  if (viewBox) {
    const parts = viewBox[1].trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
      const w = parts[2];
      const h = parts[3];
      if (w > 0 && h > 0) return { nw: w, nh: h };
    }
  }

  const parseLen = (name) => {
    const m = tag.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']+)["']`, 'i'));
    if (!m) return null;
    const num = parseFloat(m[1]);
    return Number.isFinite(num) && num > 0 ? num : null;
  };
  const w = parseLen('width');
  const h = parseLen('height');
  if (w && h) return { nw: w, nh: h };
  return null;
}

/**
 * Natural size for canvas rasterization (SVG <img> often reports 300×150 until viewBox is applied).
 * @param {object} [hints]
 * @param {boolean} [hints.isVector]
 * @param {string} [hints.mimeType]
 * @param {number} [hints.widthPx]
 * @param {number} [hints.heightPx]
 * @param {number} [hints.boxW]
 * @param {number} [hints.boxH]
 */
export function intrinsicSizeForRasterize(img, hints = {}) {
  const src = img.currentSrc || img.src || '';
  let nw = img.naturalWidth;
  let nh = img.naturalHeight;
  const isSvg =
    hints.isVector
    || String(hints.mimeType || '').includes('svg')
    || src.includes('image/svg+xml')
    || /\.svg(\?|#|$)/i.test(src);

  if (isSvg) {
    const parsed = parseSvgIntrinsicPixels(decodeSvgMarkupFromImageSrc(src));
    if (parsed) return parsed;
    const catalogW = Number(hints.widthPx);
    const catalogH = Number(hints.heightPx);
    if (catalogW > 0 && catalogH > 0) return { nw: catalogW, nh: catalogH };
    const cw = Math.max(1, Math.round(Number(hints.boxW) || 0));
    const ch = Math.max(1, Math.round(Number(hints.boxH) || 0));
    if ((!nw || !nh || (nw === 300 && nh === 150)) && cw && ch) {
      return { nw: cw, nh: ch };
    }
  }

  if (!nw || !nh) return null;
  return { nw, nh };
}

/**
 * Rasterize object-fit:cover + object-position into a PNG data URL (for html2canvas export).
 */
export function bakeCoverImageToDataUrl(img, boxW, boxH, objectPosition, zoom = 1) {
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  const cw = Math.max(1, Math.round(Number(boxW) || 0));
  const ch = Math.max(1, Math.round(Number(boxH) || 0));
  if (!nw || !nh || !cw || !ch) return null;

  // Zooming about the object-position point keeps the same offset formula with a larger scale.
  const fitScale = Math.max(cw / nw, ch / nh) * (Number(zoom) > 0 ? Number(zoom) : 1);
  const sw = nw * fitScale;
  const sh = nh * fitScale;
  const { x, y } = parseObjectPosition(objectPosition || img.style.objectPosition || '50% 50%');
  const ox = (cw - sw) * (x / 100);
  const oy = (ch - sh) * (y / 100);

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, ox, oy, sw, sh);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Rasterize object-fit:contain + object-position into a PNG data URL (for html2canvas export).
 */
export function bakeContainImageToDataUrl(img, boxW, boxH, objectPosition, rasterHints, zoom = 1) {
  const cw = Math.max(1, Math.round(Number(boxW) || 0));
  const ch = Math.max(1, Math.round(Number(boxH) || 0));
  const intrinsic = intrinsicSizeForRasterize(img, { ...rasterHints, boxW: cw, boxH: ch });
  if (!intrinsic || !cw || !ch) return null;
  const { nw, nh } = intrinsic;

  const fitScale = Math.min(cw / nw, ch / nh) * (Number(zoom) > 0 ? Number(zoom) : 1);
  const sw = nw * fitScale;
  const sh = nh * fitScale;
  const { x, y } = parseObjectPosition(objectPosition || img.style.objectPosition || '50% 50%');
  const ox = (cw - sw) * (x / 100);
  const oy = (ch - sh) * (y / 100);

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, cw, ch);
  ctx.drawImage(img, ox, oy, sw, sh);
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Rasterize object-fit:contain without letterbox — canvas matches drawn image pixels.
 * @returns {{ dataUrl: string, width: number, height: number } | null}
 */
export function bakeContainImageTightToDataUrl(img, boxW, boxH, objectPosition, rasterHints) {
  const cw = Math.max(1, Math.round(Number(boxW) || 0));
  const ch = Math.max(1, Math.round(Number(boxH) || 0));
  const intrinsic = intrinsicSizeForRasterize(img, { ...rasterHints, boxW: cw, boxH: ch });
  if (!intrinsic || !cw || !ch) return null;
  const { nw, nh } = intrinsic;

  const fitScale = Math.min(cw / nw, ch / nh);
  const sw = Math.max(1, Math.round(nw * fitScale));
  const sh = Math.max(1, Math.round(nh * fitScale));
  const { x, y } = parseObjectPosition(objectPosition || img.style.objectPosition || '50% 50%');
  const ox = (cw - nw * fitScale) * (x / 100);
  const oy = (ch - nh * fitScale) * (y / 100);

  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const drawW = nw * fitScale;
  const drawH = nh * fitScale;
  ctx.drawImage(img, ox, oy, drawW, drawH, 0, 0, sw, sh);
  try {
    return { dataUrl: canvas.toDataURL('image/png'), width: sw, height: sh };
  } catch {
    return null;
  }
}
